import { Suspense, lazy, useEffect, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import ProtectedRoute from "./components/ProtectedRoute";

// Lazy-load framer-motion to keep it out of the critical bundle
const LazyMotionWrapper = lazy(() =>
  import("framer-motion").then((mod) => ({
    default: ({ children, locationKey }: { children: React.ReactNode; locationKey: string }) => (
      <mod.AnimatePresence mode="wait">
        <mod.motion.div
          key={locationKey}
          variants={{
            initial: { opacity: 0, y: 8 },
            animate: { opacity: 1, y: 0 },
            exit: { opacity: 0, y: -8 },
          }}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="min-h-[100dvh]"
        >
          {children}
        </mod.motion.div>
      </mod.AnimatePresence>
    ),
  }))
);

// Lazy-load all page components
const Index = lazy(() => import("./pages/Index"));
const Login = lazy(() => import("./pages/Login"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const History = lazy(() => import("./pages/History"));
const BuildTeam = lazy(() => import("./pages/BuildTeam"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const UploadLink = lazy(() => import("./pages/UploadLink"));
const NotFound = lazy(() => import("./pages/NotFound"));
const TryFree = lazy(() => import("./pages/TryFree"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const HowItWorksPage = lazy(() => import("./pages/HowItWorksPage"));
const AIEmployeeHeadshots = lazy(() => import("./pages/seo/AIEmployeeHeadshots"));
const CompanyHeadshots = lazy(() => import("./pages/seo/CompanyHeadshots"));
const TeamHeadshots = lazy(() => import("./pages/seo/TeamHeadshots"));
const ProfessionalEmployeePhotos = lazy(() => import("./pages/seo/ProfessionalEmployeePhotos"));
const BusinessHeadshotsNoPhotoshoot = lazy(() => import("./pages/seo/BusinessHeadshotsNoPhotoshoot"));

const queryClient = new QueryClient();

const AnimatedRoutes = () => {
  const location = useLocation();

  const routeContent = (
    <Suspense fallback={<div className="min-h-[100dvh] bg-background" />}>
      <Routes location={location}>
        <Route path="/" element={<Index />} />
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/build" element={<ProtectedRoute><BuildTeam /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><History /></ProtectedRoute>} />
        <Route path="/history" element={<Navigate to="/dashboard" replace />} />
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route path="/try-free" element={<ProtectedRoute><TryFree /></ProtectedRoute>} />
        <Route path="/upload/:token" element={<UploadLink />} />
        {/* Supporting pages */}
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/how-it-works" element={<HowItWorksPage />} />
        {/* SEO landing pages */}
        <Route path="/ai-employee-headshots" element={<AIEmployeeHeadshots />} />
        <Route path="/company-headshots" element={<CompanyHeadshots />} />
        <Route path="/team-headshots" element={<TeamHeadshots />} />
        <Route path="/professional-employee-photos" element={<ProfessionalEmployeePhotos />} />
        <Route path="/business-headshots-no-photoshoot" element={<BusinessHeadshotsNoPhotoshoot />} />
        {/* Redirect old routes */}
        <Route path="/teams" element={<Navigate to="/build" replace />} />
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );

  return (
    <Suspense fallback={<div className="min-h-[100dvh] bg-background" />}>
      <LazyMotionWrapper locationKey={location.pathname}>
        {routeContent}
      </LazyMotionWrapper>
    </Suspense>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <CurrencyProvider>
        <BrowserRouter>
          <AuthProvider>
            <AnimatedRoutes />
          </AuthProvider>
        </BrowserRouter>
      </CurrencyProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
